import React from 'react';
import { FiX, FiPrinter, FiShare2 } from 'react-icons/fi';

const OrderDetailModal = ({
  show,
  order,
  onClose,
  onShare,
  onPrint,
  onRemotePrint,
  printerOnline,
  remotePrinting,
  isMobile,
  formatCurrency: formatCurrencyProp
}) => {
  if (!show || !order) return null;

  // Use provided formatCurrency or default
  const formatCurrency = formatCurrencyProp || ((amount) => 
    new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0))
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-green-50">
          <h2 className="text-xl font-bold text-green-800">Chi tiết đơn hàng</h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-green-200 rounded"
          >
            <FiX size={24} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto flex-1">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <span className="text-gray-500">Bàn:</span>
              <p className="font-bold">{order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi')}</p>
            </div>
            <div>
              <span className="text-gray-500">Khách hàng:</span>
              <p className="font-bold">{order.customer?.name || order.customerName || '-'}</p>
              {order.customer?.balance > 0 && (
                <span className="text-xs text-blue-600">Tiền thừa: {formatCurrency(order.customer.balance)}</span>
              )}
            </div>
            <div>
              <span className="text-gray-500">Thời gian đặt:</span>
              <p className="font-medium">{order.createdAt ? new Date(order.createdAt).toLocaleString('vi-VN') : '-'}</p>
            </div>
            {order.deliveryAt && (
              <div className="col-span-2">
                <span className="text-gray-500">⏰ Hẹn giao:</span>
                <p className="font-medium text-red-600">
                  {new Date(order.deliveryAt).toLocaleString('vi-VN')}
                </p>
              </div>
            )}
            <div>
              <span className="text-gray-500">Thanh toán lúc:</span>
              <p className="font-medium">{(order.paidAt || order.completedAt) ? new Date(order.paidAt || order.completedAt).toLocaleString('vi-VN') : '-'}</p>
            </div>
            <div>
              <span className="text-gray-500">Hình thức:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                order.paymentMethod === 'transfer' 
                  ? 'bg-blue-100 text-blue-700' 
                  : order.paymentMethod === 'debt'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {order.paymentMethod === 'transfer' 
                  ? (order.bankAccount?.accountName 
                      ? `CK: ${order.bankAccount.accountName} - ${order.bankAccount.accountNumber}`
                      : 'Chuyển khoản')
                  : order.paymentMethod === 'debt' ? 'Ghi nợ' : 'Tiền mặt'}
              </span>
            </div>
            
            {/* Cash payment details */}
            {order.paymentMethod === 'cash' && order.amountReceived > 0 && (
              <>
                <div>
                  <span className="text-gray-500">Tiền nhận: </span>
                  <span className="font-medium text-green-600">{formatCurrency(order.amountReceived)}</span>
                </div>
                {order.changeAmount > 0 && (
                  <div>
                    <span className="text-gray-500">Tiền trả lại: </span>
                    <span className="font-medium text-blue-600">{formatCurrency(order.changeAmount)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Balance used */}
            {order.balanceUsed > 0 && (
              <div>
                <span className="text-gray-500">Dùng tiền thừa: </span>
                <span className="font-medium text-blue-600">-{formatCurrency(order.balanceUsed)}</span>
              </div>
            )}
            
            {/* Balance added */}
            {order.balanceAdded > 0 && (
              <div>
                <span className="text-gray-500">Lưu tiền thừa: </span>
                <span className="font-medium text-blue-600">+{formatCurrency(order.balanceAdded)}</span>
              </div>
            )}
            
            {/* Debt info */}
            {order.debtAmount > 0 && (
              <div>
                <span className="text-gray-500">Ghi nợ: </span>
                <span className={`font-bold ${order.debtPaidAt ? 'text-green-600' : 'text-orange-600'}`}>
                  {formatCurrency(order.debtAmount)}
                  {order.debtPaidAt && ' (Đã trả)'}
                </span>
              </div>
            )}
            
            {/* Debt paid info */}
            {order.debtPaidAt && (
              <div>
                <span className="text-gray-500">Trả nợ lúc: </span>
                <span className="font-medium text-green-600">{new Date(order.debtPaidAt).toLocaleString('vi-VN')}</span>
              </div>
            )}
            {order.debtPaidMethod && order.debtPaidAt && (
              <div>
                <span className="text-gray-500">Trả nợ qua: </span>
                <span className={`font-medium ${order.debtPaidMethod === 'transfer' ? 'text-blue-600' : 'text-green-600'}`}>
                  {order.debtPaidMethod === 'transfer' 
                    ? (order.debtPaidBankAccount?.accountName 
                        ? `CK: ${order.debtPaidBankAccount.accountName}`
                        : 'Chuyển khoản')
                    : 'Tiền mặt'}
                </span>
              </div>
            )}
            
            {/* Points used */}
            {order.pointsUsed > 0 && (
              <div>
                <span className="text-gray-500">Dùng điểm: </span>
                <span className="font-medium text-amber-600">{order.pointsUsed} điểm</span>
              </div>
            )}
            {order.pointsDiscount > 0 && (
              <div>
                <span className="text-gray-500">Giảm (điểm): </span>
                <span className="font-medium text-green-600">-{formatCurrency(order.pointsDiscount)}</span>
              </div>
            )}
            {order.pointsEarned > 0 && (
              <div>
                <span className="text-gray-500">Điểm nhận: </span>
                <span className="font-medium text-blue-600">+{order.pointsEarned} điểm</span>
              </div>
            )}
            
            {/* Order code */}
            {(order.orderCode || order.importCode) && (
              <div className="col-span-2">
                <span className="text-gray-500">Mã đơn hàng:</span>
                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded inline-block ml-2">{order.orderCode || order.importCode}</span>
              </div>
            )}
          </div>

          {/* Customer Note */}
          {order.customerNote && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
              <span className="text-gray-500 text-sm">Ghi chú:</span>
              <p className="text-yellow-800">{order.customerNote}</p>
            </div>
          )}

          {/* Order Items */}
          <div className="border-t pt-4">
            <h3 className="font-bold mb-3">Danh sách món ({order.items?.length || 0})</h3>
            <div className="space-y-3">
              {(order.items || []).map((item, idx) => {
                const itemToppings = item.toppings || item.selectedToppings || [];
                return (
                <div key={idx} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    {item.selectedOption && (
                      <p className="text-sm text-gray-500">Size: {typeof item.selectedOption === 'object' ? item.selectedOption.name : item.selectedOption}</p>
                    )}
                    {itemToppings.length > 0 && itemToppings.map((t, tidx) => (
                      <p key={tidx} className="text-sm text-purple-600">
                        + {t.name} {t.price > 0 && <span className="text-purple-500">(+{formatCurrency(t.price)})</span>}
                      </p>
                    ))}
                    {item.note && (
                      <p className="text-sm text-orange-600 italic">"{item.note}"</p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-gray-500">x{item.quantity || 1}</p>
                    <p className="font-bold text-green-600">{formatCurrency((item.price || 0) * (item.quantity || 1))}</p>
                  </div>
                </div>
              )})}
            </div>
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t border-gray-300 space-y-2">
            {/* Show discount if applied */}
            {order.discount && order.discount.amount > 0 && (
              <>
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>Tổng tiền hàng:</span>
                  <span>{formatCurrency(order.originalAmount || (order.totalAmount + order.discount.amount + (order.manualDiscount?.amount || 0)))}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>🎁 {order.discount.name || `Giảm ${order.discount.percentage}%`}:</span>
                  <span>-{formatCurrency(order.discount.amount)}</span>
                </div>
              </>
            )}
            {/* Show manual discount if applied */}
            {order.manualDiscount && order.manualDiscount.amount > 0 && (
              <div className="flex justify-between items-center text-sm text-green-600">
                <span>🏷️ Giảm giá ({order.manualDiscount.discountType === 'percent' ? `${order.manualDiscount.value}%` : 'cố định'}):</span>
                <span>-{formatCurrency(order.manualDiscount.amount)}</span>
              </div>
            )}
            {/* Show extra fee if applied */}
            {order.extraFee && order.extraFee.amount > 0 && (
              <div className="flex justify-between items-center text-sm text-orange-600">
                <span>💰 {order.extraFee.description || 'Phí thêm'}:</span>
                <span>+{formatCurrency(order.extraFee.amount)}</span>
              </div>
            )}
            {order.pointsDiscount > 0 && (
              <>
                {!order.discount?.amount && (
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Tổng tiền hàng:</span>
                    <span>{formatCurrency(order.totalAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>Giảm ({order.pointsUsed} điểm):</span>
                  <span>-{formatCurrency(order.pointsDiscount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center text-lg">
              <span className="font-bold">{(order.pointsDiscount > 0 || order.discount?.amount > 0) ? 'Thực thu:' : 'Tổng cộng:'}</span>
              <span className="font-bold text-green-600 text-xl">
                {formatCurrency(order.totalAmount - (order.pointsDiscount || 0))}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-gray-50">
          {/* Printer status - mobile only, only if print functions provided */}
          {isMobile && (onPrint || onRemotePrint) && (
            <div className={`text-center text-xs mb-2 ${printerOnline ? 'text-green-600' : 'text-gray-400'}`}>
              {printerOnline ? '🟢 Máy in sẵn sàng' : '🔴 Máy in chưa kết nối'}
            </div>
          )}
          <div className="flex space-x-2">
            {/* Print buttons - only show if handlers provided */}
            {(onPrint || onRemotePrint) && (
              <>
                {isMobile ? (
                  /* Mobile: Single print button - remote */
                  <button
                    onClick={() => (onRemotePrint || onPrint)(order)}
                    disabled={onRemotePrint && (!printerOnline || remotePrinting)}
                    className={`flex-1 py-2 rounded-lg flex items-center justify-center text-sm ${
                      !onRemotePrint || printerOnline 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    } ${remotePrinting ? 'opacity-50' : ''}`}
                  >
                    <FiPrinter className="mr-1" /> 
                    {remotePrinting ? 'Đang in...' : 'In hóa đơn'}
                  </button>
                ) : (
                  /* Desktop: Two print buttons */
                  <>
                    {onPrint && (
                      <button
                        onClick={() => onPrint(order)}
                        className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm"
                      >
                        <FiPrinter className="mr-1" /> In tại đây
                      </button>
                    )}
                    {onRemotePrint && (
                      <button
                        onClick={() => onRemotePrint(order)}
                        disabled={!printerOnline || remotePrinting}
                        className={`flex-1 py-2 rounded-lg flex items-center justify-center text-sm ${
                          printerOnline 
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        } ${remotePrinting ? 'opacity-50' : ''}`}
                      >
                        <FiPrinter className="mr-1" /> 
                        {remotePrinting ? 'Đang in...' : 'In tại quán'}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
            
            {/* Share button */}
            {onShare && (
              <button
                onClick={() => onShare(order)}
                className="py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center text-sm"
                title="Chia sẻ"
              >
                <FiShare2 className="mr-1" /> Share
              </button>
            )}
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
